import { useState } from "react";
import { toast } from "sonner";
import { api, imgUrl } from "@/lib/api";
import { useData } from "@/context/DataContext";
import { motion } from "framer-motion";
import { X, Upload, Loader2, Save, ImagePlus } from "lucide-react";

const EMPTY = {
  name: "", category: "", description: "",
  hero_image_url: "", logo_url: "", gallery: [],
  phone: "", email: "", whatsapp: "", facebook: "", instagram: "", tiktok: "", website: "", menu_url: "",
  street: "", colonia: "", city: "", state: "", postal_code: "", full_address: "",
  google_maps_url: "", lat: "", lng: "",
  discount: { title: "", description: "", badge: "" },
  membership_id: "", featured: false, is_active: true,
};

function Field({ label, ...props }) {
  return (
    <label className="flex flex-col gap-1">
      <span className="text-xs font-bold text-[#5C5C5C]">{label}</span>
      <input {...props} className="rounded-lg border border-black/10 px-3 py-2 text-sm outline-none focus:border-[#E54D2E]" />
    </label>
  );
}

export default function BusinessForm({ initial, onClose, onSaved }) {
  const { categories, memberships } = useData();
  const [form, setForm] = useState(() => ({
    ...EMPTY,
    ...(initial || {}),
    category: initial?.category || categories[0]?.slug || "",
    discount: { ...EMPTY.discount, ...(initial?.discount || {}) },
  }));
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [galUploading, setGalUploading] = useState(false);
  const [error, setError] = useState("");

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const setDisc = (k, v) => setForm((f) => ({ ...f, discount: { ...f.discount, [k]: v } }));

  const upload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    setError("");
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/admin/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      set("hero_image_url", data.url);
    } catch (err) {
      setError("Error al subir la imagen.");
    } finally {
      setUploading(false);
    }
  };

  const uploadGallery = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    setGalUploading(true);
    setError("");
    try {
      for (const file of files) {
        const fd = new FormData();
        fd.append("file", file);
        const { data } = await api.post("/admin/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
        setForm((f) => ({ ...f, gallery: [...f.gallery, data.url] }));
      }
    } catch (err) {
      setError("Error al subir una imagen de la galería.");
    } finally {
      setGalUploading(false);
    }
  };

  const removeGalleryItem = (idx) => {
    setForm((f) => ({ ...f, gallery: f.gallery.filter((_, i) => i !== idx) }));
  };

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError("");
    const payload = {
      ...form,
      lat: form.lat === "" ? null : parseFloat(form.lat),
      lng: form.lng === "" ? null : parseFloat(form.lng),
      full_address: form.full_address || [form.street, form.colonia, form.city, form.state].filter(Boolean).join(", "),
    };
    try {
      if (initial?.id) await api.put(`/admin/businesses/${initial.id}`, payload);
      else await api.post("/admin/businesses", payload);
      toast.success(initial?.id ? "Negocio actualizado" : "Negocio creado");
      onSaved();
    } catch (err) {
      setError("No se pudo guardar. Revisa los campos obligatorios.");
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[80] flex justify-center overflow-y-auto bg-black/50 p-0 sm:p-4">
      <motion.div
        initial={{ opacity: 0, y: 30 }}
        animate={{ opacity: 1, y: 0 }}
        className="h-fit min-h-screen w-full max-w-lg bg-white sm:min-h-0 sm:rounded-2xl"
        data-testid="business-form"
      >
        <div className="sticky top-0 z-10 flex items-center justify-between border-b border-black/5 bg-white px-5 py-4">
          <h2 className="font-display text-xl font-bold">{initial?.id ? "Editar negocio" : "Nuevo negocio"}</h2>
          <button onClick={onClose} data-testid="form-close" className="rounded-full bg-[#F3F0E6] p-2"><X size={18} /></button>
        </div>

        <form onSubmit={submit} className="flex flex-col gap-4 p-5">
          {/* Image */}
          <div>
            <span className="text-xs font-bold text-[#5C5C5C]">Imagen principal</span>
            <div className="mt-1 flex items-center gap-3">
              <div className="h-20 w-28 overflow-hidden rounded-lg border border-black/10 bg-[#F3F0E6]">
                {form.hero_image_url && <img src={imgUrl(form.hero_image_url)} alt="" className="h-full w-full object-cover" />}
              </div>
              <label className="flex cursor-pointer items-center gap-2 rounded-lg bg-[#1A1A1A] px-3 py-2 text-sm font-bold text-white" data-testid="upload-image-label">
                {uploading ? <Loader2 size={16} className="animate-spin" /> : <Upload size={16} />}
                Subir foto
                <input type="file" accept="image/*" onChange={upload} className="hidden" data-testid="upload-image-input" />
              </label>
            </div>
            <input value={form.hero_image_url} onChange={(e) => set("hero_image_url", e.target.value)} placeholder="o pega una URL de imagen" className="mt-2 w-full rounded-lg border border-black/10 px-3 py-2 text-sm outline-none" data-testid="hero-url-input" />
          </div>

          {/* Gallery */}
          <div>
            <div className="flex items-center justify-between">
              <span className="text-xs font-bold text-[#5C5C5C]">Galería de fotos</span>
              <label className="flex cursor-pointer items-center gap-1.5 rounded-lg bg-[#F3F0E6] px-3 py-1.5 text-xs font-bold" data-testid="gallery-upload-label">
                {galUploading ? <Loader2 size={14} className="animate-spin" /> : <ImagePlus size={14} />}
                Agregar fotos
                <input type="file" accept="image/*" multiple onChange={uploadGallery} className="hidden" data-testid="gallery-upload-input" />
              </label>
            </div>
            {form.gallery.length > 0 && (
              <div className="no-scrollbar mt-2 flex gap-2 overflow-x-auto">
                {form.gallery.map((g, i) => (
                  <div key={i} className="relative h-20 w-24 shrink-0 overflow-hidden rounded-lg border border-black/10">
                    <img src={imgUrl(g)} alt="" className="h-full w-full object-cover" />
                    <button type="button" onClick={() => removeGalleryItem(i)} data-testid={`gallery-remove-${i}`} className="absolute right-1 top-1 rounded-full bg-black/60 p-1 text-white">
                      <X size={12} />
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>

          <Field label="Nombre *" value={form.name} onChange={(e) => set("name", e.target.value)} required data-testid="form-name" />
          <label className="flex flex-col gap-1">
            <span className="text-xs font-bold text-[#5C5C5C]">Categoría *</span>
            <select value={form.category} onChange={(e) => set("category", e.target.value)} data-testid="form-category" className="rounded-lg border border-black/10 px-3 py-2 text-sm outline-none">
              {categories.map((c) => <option key={c.slug} value={c.slug}>{c.label}</option>)}
            </select>
          </label>
          <label className="flex flex-col gap-1">
            <span className="text-xs font-bold text-[#5C5C5C]">Nivel de membresía</span>
            <select value={form.membership_id} onChange={(e) => set("membership_id", e.target.value)} data-testid="form-membership" className="rounded-lg border border-black/10 px-3 py-2 text-sm outline-none">
              <option value="">Sin membresía</option>
              {memberships.map((m) => <option key={m.id} value={m.id}>{m.name}{m.is_featured ? " (Destacado)" : ""}</option>)}
            </select>
          </label>
          <label className="flex flex-col gap-1">
            <span className="text-xs font-bold text-[#5C5C5C]">Descripción</span>
            <textarea value={form.description} onChange={(e) => set("description", e.target.value)} rows={3} data-testid="form-description" className="rounded-lg border border-black/10 px-3 py-2 text-sm outline-none focus:border-[#E54D2E]" />
          </label>

          <p className="text-xs font-bold uppercase tracking-widest text-[#1A1A1A]">Promoción</p>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Badge (2x1, 15% OFF)" value={form.discount.badge} onChange={(e) => setDisc("badge", e.target.value)} data-testid="form-badge" />
            <Field label="Título promo" value={form.discount.title} onChange={(e) => setDisc("title", e.target.value)} data-testid="form-promo-title" />
          </div>
          <Field label="Detalle promo" value={form.discount.description} onChange={(e) => setDisc("description", e.target.value)} />

          <p className="text-xs font-bold uppercase tracking-widest text-[#1A1A1A]">Contacto y redes</p>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Teléfono" value={form.phone} onChange={(e) => set("phone", e.target.value)} />
            <Field label="WhatsApp (con lada)" value={form.whatsapp} onChange={(e) => set("whatsapp", e.target.value)} data-testid="form-whatsapp" />
            <Field label="Correo" value={form.email} onChange={(e) => set("email", e.target.value)} />
            <Field label="Sitio web" value={form.website} onChange={(e) => set("website", e.target.value)} />
            <Field label="Facebook" value={form.facebook} onChange={(e) => set("facebook", e.target.value)} />
            <Field label="Instagram" value={form.instagram} onChange={(e) => set("instagram", e.target.value)} />
            <Field label="TikTok" value={form.tiktok} onChange={(e) => set("tiktok", e.target.value)} />
            <Field label="Menú (URL)" value={form.menu_url} onChange={(e) => set("menu_url", e.target.value)} />
          </div>

          <p className="text-xs font-bold uppercase tracking-widest text-[#1A1A1A]">Ubicación</p>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Calle y número" value={form.street} onChange={(e) => set("street", e.target.value)} />
            <Field label="Colonia" value={form.colonia} onChange={(e) => set("colonia", e.target.value)} />
            <Field label="Ciudad" value={form.city} onChange={(e) => set("city", e.target.value)} data-testid="form-city" />
            <Field label="Estado" value={form.state} onChange={(e) => set("state", e.target.value)} data-testid="form-state" />
            <Field label="C.P." value={form.postal_code} onChange={(e) => set("postal_code", e.target.value)} />
            <Field label="Google Maps URL" value={form.google_maps_url} onChange={(e) => set("google_maps_url", e.target.value)} />
            <Field label="Latitud" value={form.lat} onChange={(e) => set("lat", e.target.value)} placeholder="20.9674" />
            <Field label="Longitud" value={form.lng} onChange={(e) => set("lng", e.target.value)} placeholder="-89.6237" />
          </div>

          <div className="flex items-center gap-4">
            <label className="flex items-center gap-2 text-sm font-semibold">
              <input type="checkbox" checked={form.featured} onChange={(e) => set("featured", e.target.checked)} data-testid="form-featured" /> Destacado
            </label>
            <label className="flex items-center gap-2 text-sm font-semibold">
              <input type="checkbox" checked={form.is_active} onChange={(e) => set("is_active", e.target.checked)} data-testid="form-active" /> Activo
            </label>
          </div>

          {error && <p className="text-sm font-semibold text-[#E54D2E]" data-testid="form-error">{error}</p>}

          <button type="submit" disabled={saving} data-testid="form-submit" className="flex items-center justify-center gap-2 rounded-xl bg-[#E54D2E] py-3.5 font-bold text-white disabled:opacity-60">
            {saving ? <Loader2 size={18} className="animate-spin" /> : <Save size={18} />}
            Guardar negocio
          </button>
        </form>
      </motion.div>
    </div>
  );
}
